import { Injectable }       from '@angular/core';
import { NgbModal }         from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                          from '../api/api.service';
import { CompanyService }                      from '../companies/company.service';
import { ConfirmService }                      from '../messages/confirm/confirm.service';
import { CurrencyService }                     from '../core/services/currency.service';
import { PurchaseRequestCostModel }            from './typings/uk-postcode.purchase-request.cost';
import { PurchaseRequestQuoteModel }           from './typings/uk-postcode.purchase-request.quote';
import { SessionService }                      from '../sessions/session.service';
import { UKPostcodeAddressDetailModel }        from './typings/uk-postcode.address.detail';
import { UKPostcodeAddressesComponent }        from './uk-postcode.addresses.component';
import { UKPostcodeAddressModel }              from './typings/uk-postcode.address';
import { UKPostcodePurchaseRequestsComponent } from './uk-postcode.purchase-requests.component';

@Injectable({
  providedIn: 'root'
})
export class UKPostcodeService {
  public title = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.UK_POSTCODE_LOOKUP.UK_POSTCODE_LOOKUP');

  private _states : Promise<Array<any>>;

  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private confirmService  : ConfirmService,
    private currencyService : CurrencyService,
    private modalService    : NgbModal,
    private sessionService  : SessionService,
    private translateService: TranslateService,
  ) {}

  activate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company/add_uk_postcode_lookup'))
    .then(() => this.sessionService.refresh());
  }

  confirmPurchase ( quote : PurchaseRequestQuoteModel ) : Promise<any> {
    return this.apiService.post('/chargify/purchase', {
      quantity: quote.quantity,
      service : 'uk_postcode'
    })
    .then(() => this.sessionService.refresh());
  }

  deactivate () : Promise<any> {
    return this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.DEACTIVATE', {
      name: this.title
    }))
    .then(() => this.apiService.get('/company/remove_uk_postcode_lookup'))
    .then(() => this.sessionService.refresh());
  }

  getAvailableRequests () : Promise<number> {
    return Promise.resolve(this.companyService.getUKPostcodeRequests());
  }

  getQuotedPrice ( cost : PurchaseRequestCostModel, quote : PurchaseRequestQuoteModel ) : string {
    return quote.quantity
      ? `${ this.currencyService.getInfo().symbol }${ cost.price * quote.quantity }`
      : null;
  }

  purchase () : Promise<any> {
    return this.apiService.get('/chargify/service_cost', 'uk_postcode')
    .then(( cost : PurchaseRequestCostModel ) => {
      const modal = this.modalService.open(UKPostcodePurchaseRequestsComponent);

      modal.componentInstance.cost = cost;

      return modal.result;
    });
  }

  // WS-7020. Most United Kingdom addresses carry the number as part of the street and have no
  // separate premises, so the common case used to leave the first address line empty and put the
  // whole street on the second. Where the service DOES return a premises, both lines are filled
  // exactly as they were before.
  addressLines ( detail : UKPostcodeAddressDetailModel ) : { address1 : string, address2 : string } {
    if (!detail) {
      return { address1: '', address2: '' };
    }

    return detail.Premises
      ? { address1: detail.Premises, address2: detail.Street || '' }
      : { address1: detail.Street || '', address2: '' };
  }

  // WS-7020. The county box is a dropdown of StateType codes while the postcode service answers
  // with a plain county name, so the name has to be resolved to a code or the box selects nothing.
  // Matched on description, which is the English county name the service also uses, rather than on
  // the translated label the dropdown displays.
  countyCode ( county : string, states : Array<any> ) : string {
    if (!county || !states) {
      return '';
    }

    const wanted  = county.trim().toLowerCase();
    const matched = states.filter(( state : any ) => ( state.description || '' ).trim().toLowerCase() === wanted);

    return matched.length ? matched[0].code : '';
  }

  // The list is the same for the whole session, so it is fetched once and reused. This is a plain
  // reference lookup and is not one of the paid postcode requests. A failure to read it costs the
  // county and nothing else, so it resolves to an empty list rather than failing the search.
  private getStates () : Promise<Array<any>> {
    if (!this._states) {
      this._states = this.apiService.get('/system/state_types', null, 'states')
      .catch(() => []);
    }

    return this._states;
  }

  search ( postcode : string ) : Promise<any> {
    return postcode
      ? this.apiService.get('/uk_postcode/retrieve_uk_postcode_addresses', postcode)
        .then(( addresses : Array<UKPostcodeAddressModel> ) => {
          const modal = this.modalService.open(UKPostcodeAddressesComponent, {
            windowClass: 'lg-modal'
          });

          modal.componentInstance.addresses = addresses;

          return modal.result;
        })
        // WS-7020. Twelve screens consume this answer and all twelve mapped it the same wrong way,
        // four of them without a states list of their own to resolve the county against. So the
        // mapping is worked out once here and handed over ready to use. Every original field is
        // still present, so nothing that already reads them has to change.
        .then(( detail : UKPostcodeAddressDetailModel ) => this.getStates()
          .then(( states : Array<any> ) => {
            const lines   = this.addressLines(detail);
            const company = this.sessionService.company();

            return Object.assign({}, detail, {
              address1   : lines.address1,
              address2   : lines.address2,
              countryCode: company ? company.country_code : '',
              stateCode  : this.countyCode(detail ? detail.County : '', states)
            });
          }))
      : Promise.reject(this.translateService.instant('UK_POSTCODE_SPACE.PROVIDE_A_POSTCODE'));
  }
}
